import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useI18n } from '../../i18n/index.jsx';
import { useCategories } from '../../data.js';
import { useContent, pickLang } from '../../content.jsx';
import RichText from '../RichText.jsx';
import { typoProps, useTypoFonts } from '../../richText.js';
import { sectorDefaults, strapOf } from '../../sectorsData.js';
import { deptPath } from '../../catalogue.js';
import './sectors.css';

// Home page "Categories we serve": one white block per sector — a photo
// panel on the left (opens the marketplace on the whole sector), its
// departments on the right (each opens its department page; up to 6
// visible, more scroll sideways with arrows / swipe) and a stats strap
// (admin's items or real counts, optional link) under them.
// Content: Admin > Website content > Sectors (built-in defaults until then).

export { CATEGORIES } from '../../sectorsData.js';

function Arrow({ dir }) {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d={dir === 'left' ? 'M15 5l-7 7 7 7' : 'M9 5l7 7-7 7'} />
    </svg>
  );
}

// Icon library for the stats strap and badges (24×24, stroked).
const ICONS = {
  users: 'M16 19v-1a4 4 0 00-4-4H6a4 4 0 00-4 4v1M9 10a3 3 0 100-6 3 3 0 000 6zm13 9v-1a4 4 0 00-3-3.87M16 4.13a3 3 0 010 5.74',
  box: 'M21 8l-9-5-9 5 9 5 9-5zM3 8v8l9 5 9-5V8M12 13v8',
  globe: 'M12 22a10 10 0 100-20 10 10 0 000 20zM2 12h20M12 2a15 15 0 010 20M12 2a15 15 0 000 20',
  truck: 'M1 4h14v11H1zM15 9h4l4 4v2h-8M5.5 19a2 2 0 100-4 2 2 0 000 4zm13 0a2 2 0 100-4 2 2 0 000 4z',
  check: 'M20 6L9 17l-5-5',
  star: 'M12 2l3.1 6.3 6.9 1-5 4.9 1.2 6.8L12 17.8 5.8 21l1.2-6.8-5-4.9 6.9-1z',
  factory: 'M2 20V9l6 4V9l6 4V4h6v16H2z',
  leaf: 'M11 20A7 7 0 014 13c0-6 7-10 16-10 0 9-4 16-10 16zM4 20c4-4 7-6 11-8',
  file: 'M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8zM14 2v6h6M8 13h8M8 17h8',
  clock: 'M12 22a10 10 0 100-20 10 10 0 000 20zM12 6v6l4 2',
  shield: 'M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10zM9 12l2 2 4-4',
  award: 'M12 15a7 7 0 100-14 7 7 0 000 14zM8.2 13.9L7 23l5-3 5 3-1.2-9.1',
  warehouse: 'M3 21V9l9-6 9 6v12M7 21v-8h10v8M7 17h10',
  handshake: 'M11 17l2 2a1 1 0 003-3M14 14l2.5 2.5a1 1 0 003-3l-3.88-3.88a3 3 0 00-4.24 0l-.88.88a1 1 0 01-3-3l2.81-2.81a5.79 5.79 0 017.06-.87l.47.28a2 2 0 001.42.25L21 4M21 3l1 11h-2M3 3L2 14l6.5 6.5a1 1 0 003-3M3 4h8',
  tag: 'M12.59 2.59A2 2 0 0011.17 2H4a2 2 0 00-2 2v7.17a2 2 0 00.59 1.42l8.7 8.7a2.43 2.43 0 003.42 0l6.58-6.58a2.43 2.43 0 000-3.42zM7.5 7.5h.01',
  'map-pin': 'M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 0116 0zM12 13a3 3 0 100-6 3 3 0 000 6z',
  ship: 'M12 10.19V14M12 2v3M19 13V7a2 2 0 00-2-2H7a2 2 0 00-2 2v6M19.38 20A11.6 11.6 0 0021 14l-8.19-3.64a2 2 0 00-1.62 0L3 14a11.6 11.6 0 002.81 7.76M2 21c.6.5 1.2 1 2.5 1 2.5 0 2.5-2 5-2 1.3 0 1.9.5 2.5 1s1.2 1 2.5 1c2.5 0 2.5-2 5-2 1.3 0 1.9.5 2.5 1',
};
export const ICON_NAMES = Object.keys(ICONS);
export function StatIcon({ name, size = 18 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d={ICONS[name] || ICONS.users} />
    </svg>
  );
}

// One sector as the block shows it (published content or the admin's draft).
export function sectorView(s, lang) {
  const strap = strapOf(s);
  return {
    key: s.key, photo: s.photo || `/sectors/${s.key}.webp`,
    name: pickLang(s.name, lang), subtitle: pickLang(s.tagline, lang),
    categories: (s.categories || '').split(',').map((c) => c.trim()).filter(Boolean),
    strap: {
      show: strap.show !== false, scroll: !!strap.scroll, speed: strap.speed || 'normal',
      items: (strap.items || []).map((x) => ({ id: x.id, icon: x.icon, iconUrl: x.iconUrl, auto: x.auto || '', text: pickLang(x.text, lang) })).filter((x) => x.auto || x.text),
      link: { text: pickLang(strap.link?.text, lang), target: strap.link?.target || 'hidden', url: strap.link?.url || '' },
    },
    // Departments with "Show on home page" on (absent = on, older documents).
    departments: (s.departments || []).filter((d) => d.onHome !== false).map((d) => ({ id: d.id, name: pickLang(d.name, lang), photo: d.photo, slug: d.categorySlug, path: deptPath(s, d, lang) })),
  };
}

// Published sectors (hidden ones left out), else the built-in defaults.
function useSectors() {
  const { lang } = useI18n();
  const { page } = useContent('sectors');
  return useMemo(() => {
    const source = page?.items?.length ? page.items : sectorDefaults().items;
    // "Hide if empty" (admin): a sector without departments isn't shown.
    return source.filter((s) => !s.hidden && !(s.hideIfEmpty && !(s.departments || []).some((d) => d.onHome !== false))).map((s) => sectorView(s, lang));
  }, [page, lang]);
}

const sectorCatsOf = (s, known) => [...new Set([...s.categories, ...s.departments.map((d) => d.slug).filter((x) => known.has(x))])].slice(0, 10);

// Real counts (approved listings, sellers) for sectors whose strap has an
// "automatic" item.
function useSectorStats(sectors, known) {
  const [stats, setStats] = useState({});
  const query = sectors.filter((s) => s.strap.show && s.strap.items.some((x) => x.auto))
    .map((s) => `${s.key}:${sectorCatsOf(s, known).join(',')}`)
    .filter((x) => !x.endsWith(':')).join(';');
  useEffect(() => {
    if (!query) return undefined;
    let alive = true;
    fetch(`/api/sector-stats?s=${encodeURIComponent(query)}`, { cache: 'no-cache' })
      .then((r) => (r.ok ? r.json() : {})).then((d) => { if (alive) setStats(d); }).catch(() => {});
    return () => { alive = false; };
  }, [query]);
  return stats;
}

export default function SectorShowcase() {
  const { text, value } = useContent('home');
  const typo = value('typo', null);
  useTypoFonts(typo);
  const tp = typoProps(typo);
  const sectors = useSectors();
  const categories = useCategories();
  const known = useMemo(() => new Set(categories.map((c) => c.slug)), [categories]);
  const stats = useSectorStats(sectors, known);
  // "See more": one sector open at a time.
  const [openKey, setOpenKey] = useState(null);
  return (
    <section className={`section sectors2 ${tp.className}`} style={tp.style} aria-labelledby="sectors-title">
      <div className="container">
        <header className="section-head">
          <h2 id="sectors-title" className="typo-title">{text('tradeTitle', 'home.tradeTitle')}</h2>
          <RichText className={`section-lead${typo ? '' : ' muted'}`} value={text('tradeLead', 'home.tradeLead')} />
        </header>
        <div className="sector-rows">
          {sectors.map((s, i) => <SectorRow key={s.key} s={s} index={i} known={known} counts={stats[s.key]}
            expanded={openKey === s.key} onToggle={() => setOpenKey((k) => (k === s.key ? null : s.key))} />)}
        </div>
      </div>
    </section>
  );
}

// Admin live preview: one sector from the draft being edited.
export function SectorPreview({ sector }) {
  const { lang } = useI18n();
  const categories = useCategories();
  const known = useMemo(() => new Set(categories.map((c) => c.slug)), [categories]);
  const view = useMemo(() => sectorView(sector, lang), [sector, lang]);
  const list = useMemo(() => [view], [view]);
  const stats = useSectorStats(list, known);
  return <div className="sectors2 sector-preview"><SectorRow s={view} index={0} known={known} counts={stats[view.key]} shownNow /></div>;
}

// First 6 departments in the row; the others in extra rows opened with
// "See more (N)" at the end of the strap.
export const CARD_DEPTS = 6;
function SectorRow({ s, index, known, counts, shownNow, expanded = false, onToggle }) {
  const { t, lang } = useI18n();
  const rowRef = useRef(null);
  const trackRef = useRef(null);
  const [shown, setShown] = useState(!!shownNow);
  const [edges, setEdges] = useState({ start: true, end: true });

  // Fade/rise in the first time the row scrolls into view.
  useEffect(() => {
    const el = rowRef.current;
    if (shownNow) return undefined;
    if (!el || !('IntersectionObserver' in window)) { setShown(true); return undefined; }
    const io = new IntersectionObserver(([e]) => { if (e.isIntersecting) { setShown(true); io.disconnect(); } }, { threshold: 0.12 });
    io.observe(el);
    return () => io.disconnect();
  }, [shownNow]);
  const updateEdges = useCallback(() => {
    const el = trackRef.current;
    if (!el) return;
    const max = el.scrollWidth - el.clientWidth;
    setEdges({ start: el.scrollLeft <= 4, end: el.scrollLeft >= max - 4 });
  }, []);
  useEffect(() => {
    updateEdges();
    window.addEventListener('resize', updateEdges);
    return () => window.removeEventListener('resize', updateEdges);
  }, [updateEdges, s.departments.length]);
  const scroll = (dir) => {
    const el = trackRef.current;
    if (!el) return;
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    el.scrollBy({ left: dir * el.clientWidth * 0.8, behavior: reduce ? 'auto' : 'smooth' });
  };

  // Photo panel → the marketplace on the sector's categories (its own and
  // its departments' existing ones). Department card → its department page.
  const sectorCats = sectorCatsOf(s, known);
  const sectorHref = sectorCats.length ? `/marketplace?category=${sectorCats.join(',')}` : '/marketplace';

  // Strap items: admin text, or the real count for "automatic" ones (shown
  // only when above zero — nothing invented). "{n}" in the text = the count.
  const strapItems = s.strap.show ? s.strap.items.map((x) => {
    if (!x.auto) return x;
    const n = counts?.[x.auto];
    if (!(n > 0)) return null;
    const text = x.text ? x.text.replace(/\{n\}/g, n.toLocaleString(lang === 'en' ? 'en-CA' : 'fr-CA'))
      : t(n === 1 ? `sectors.${x.auto === 'sellers' ? 'statSellers' : 'statListings'}One` : `sectors.${x.auto === 'sellers' ? 'statSellers' : 'statListings'}`, { n });
    return { ...x, text };
  }).filter(Boolean) : [];
  const { link } = s.strap;
  const linkHref = link.target === 'marketplace' ? sectorHref
    : link.target === 'sector' ? `/${lang === 'en' ? 'sectors' : 'secteurs'}/${s.key}`
      : link.target === 'url' ? link.url : null;
  const linkText = link.text || t('sectors.viewAll', { name: s.name.toLowerCase() });
  const first = s.departments.slice(0, CARD_DEPTS);
  const more = s.departments.slice(CARD_DEPTS);
  const showStrap = (s.strap.show && (strapItems.length > 0 || !!linkHref)) || more.length > 0;
  const moreId = `sector-more-${s.key}`;
  const tile = (d, j) => (
    <li key={d.id || j}>
      <Link to={d.path} className="dept-card">
        <span className={`dept-img${d.photo ? '' : ' is-empty'}`} style={{ '--h': (index * 47 + j * 23) % 360 }}>
          {d.photo ? <img src={d.photo} alt="" loading="lazy" decoding="async" /> : <span aria-hidden="true">{d.name.slice(0, 1)}</span>}
        </span>
        <span className="dept-name">{d.name}</span>
      </Link>
    </li>
  );
  const overflow = !(edges.start && edges.end);

  return (
    <article ref={rowRef} id={`sector-${s.key}`} className={`sector-row2${shown ? ' is-shown' : ''}`} style={{ '--i': index }}>
      <Link to={sectorHref} className="sector-hero2" aria-label={t('sectors.viewAll', { name: s.name })}>
        <img src={s.photo} alt="" loading="lazy" decoding="async" />
        <span className="sector-hero2-shade" aria-hidden="true" />
        <span className="sector-hero2-text">
          <span className="sector-hero2-name">{s.name}</span>
          {s.subtitle && <span className="sector-hero2-sub">{s.subtitle}</span>}
        </span>
        <span className="sector-hero2-go" aria-hidden="true"><Arrow dir="right" /></span>
      </Link>
      <div className="sector-side2">
        {s.departments.length > 0 ? (
          <div className="dept-scroller">
            {overflow && <button type="button" className="round-btn dept-arrow is-left" onClick={() => scroll(-1)} disabled={edges.start} aria-label={t('sectors.scrollPrev')}><Arrow dir="left" /></button>}
            <ul ref={trackRef} className="dept-row" onScroll={updateEdges} aria-label={t('sectors.departmentsOf', { name: s.name })}>
              {first.map(tile)}
            </ul>
            {overflow && <button type="button" className="round-btn dept-arrow is-right" onClick={() => scroll(1)} disabled={edges.end} aria-label={t('sectors.scrollNext')}><Arrow dir="right" /></button>}
          </div>
        ) : (
          // No departments yet: placeholder tiles of the same size, so every
          // sector block is exactly as tall as the others.
          <div className="dept-scroller dept-empty">
            <ul className="dept-row is-placeholder" aria-hidden="true">
              {Array.from({ length: 6 }, (_, j) => (
                <li key={j}><span className="dept-card"><span className="dept-img is-ghost" /><span className="dept-name">&nbsp;</span></span></li>
              ))}
            </ul>
            <p className="dept-empty-text">{t('sectors.departmentsSoon')}</p>
          </div>
        )}
        {more.length > 0 && (
          <div id={moreId} className={`dept-more${expanded ? ' is-open' : ''}`} {...(expanded ? {} : { inert: '', 'aria-hidden': true })}>
            <div className="dept-more-inner">
              <ul className="dept-grid" aria-label={t('sectors.moreDepartments', { name: s.name })}>{more.map((d, j) => tile(d, j + CARD_DEPTS))}</ul>
            </div>
          </div>
        )}
        {/* Same height with or without a strap: an empty one keeps its place. */}
        {!showStrap && <div className="sector-strap is-placeholder" aria-hidden="true" />}
        {showStrap && (
          <div className="sector-strap">
            {strapItems.length > 0 && <StrapItems items={strapItems} scroll={s.strap.scroll} speed={s.strap.speed} />}
            {linkHref && (/^https:/.test(linkHref)
              ? <a href={linkHref} className="strap-link" target="_blank" rel="noopener noreferrer">{linkText} <Arrow dir="right" /></a>
              : <Link to={linkHref} className="strap-link">{linkText} <Arrow dir="right" /></Link>)}
            {more.length > 0 && (
              <button type="button" className={`strap-more${linkHref ? '' : ' is-alone'}`} aria-expanded={expanded} aria-controls={moreId} onClick={onToggle}>
                {expanded ? t('sectors.seeLess') : t('sectors.seeMore', { n: more.length })}
                <svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true"><path d="M6 9l6 6 6-6" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" /></svg>
              </button>
            )}
          </div>
        )}
      </div>
    </article>
  );
}

// Stat items; when they don't fit (or the admin turned scrolling on) they
// move right-to-left in a seamless loop: enough copies to fill the bar,
// shifted by exactly one copy. Pauses on hover; static with reduced motion.
const STRAP_PX_PER_S = { slow: 22, normal: 38, fast: 60 };
function StrapItems({ items, scroll, speed }) {
  const boxRef = useRef(null);
  const listRef = useRef(null);
  const [m, setM] = useState({ box: 0, list: 0 });
  const [reduce, setReduce] = useState(() => typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
  useEffect(() => {
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    const on = () => setReduce(mq.matches);
    mq.addEventListener('change', on);
    return () => mq.removeEventListener('change', on);
  }, []);
  useEffect(() => {
    const measure = () => { if (boxRef.current && listRef.current) setM({ box: boxRef.current.clientWidth, list: listRef.current.scrollWidth }); };
    measure();
    if (!('ResizeObserver' in window)) return undefined;
    const ro = new ResizeObserver(measure);
    if (boxRef.current) ro.observe(boxRef.current);
    if (listRef.current) ro.observe(listRef.current);
    return () => ro.disconnect();
  }, [items]);
  const fits = m.list > 0 && m.list <= m.box + 1;
  const moving = !reduce && m.list > 0 && (scroll || !fits);
  const copies = moving ? Math.max(2, Math.ceil(m.box / m.list) + 1) : 1;
  const style = moving ? { '--strap-shift': `-${m.list}px`, '--strap-dur': `${Math.max(8, m.list / (STRAP_PX_PER_S[speed] || 38))}s` } : undefined;
  const render = (k) => (
    <ul key={k} ref={k === 0 ? listRef : undefined} className="strap-list" aria-hidden={k > 0 || undefined}>
      {items.map((x, i) => (
        <li key={x.id || i}>
          <span className={`strap-icon c${i % 6}`}>{x.iconUrl ? <img src={x.iconUrl} alt="" /> : <StatIcon name={x.icon} size={16} />}</span>
          {x.text}
        </li>
      ))}
    </ul>
  );
  return (
    <div ref={boxRef} className={`strap-viewport${moving ? ' is-moving' : ''}`}>
      <div className="strap-track" style={style}>{Array.from({ length: copies }, (_, k) => render(k))}</div>
    </div>
  );
}
